import tokens from '@kluein/design-tokens';
import { Check, Warning, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { DigestPickerModal } from 'components/common/digest-picker-modal';
import { NotifierType, useNotifier } from 'hooks/use-notifier';
import { useAddCardToDigestMutation } from 'store/api/digests';

import type { DigestPickerSubmitResult } from 'components/common/digest-picker-modal';
import type { ReactNode } from 'react';

export type AddToDigestModalProps = {
  cardId: number;
  // Card body HTML — used by the shared picker to detect sanitizer-stripped
  // elements (iframes, tables, etc.) and prompt for confirmation before the
  // add. Optional because callers that don't have the card body loaded yet
  // just skip the warning; the add still proceeds.
  contentHtml?: string;
  onClose: () => void;
};

// Thin card-adapter over the shared DigestPickerModal. Owns the mutation and
// toast copy; presentation (modal shell, digest list, checkbox picker) comes
// from the shared component. The backend infers split-vs-single from the
// card's <section data-article-url> markers — no `mode` in the payload.
const AddToDigestModal = ({
  cardId,
  contentHtml,
  onClose,
}: AddToDigestModalProps): ReactNode => {
  const { t } = useTranslation(['Card', 'Common']);
  const { notify } = useNotifier();
  const [addCardToDigest] = useAddCardToDigestMutation();

  const handleSubmit = async (
    digestIds: number[],
  ): Promise<DigestPickerSubmitResult> => {
    const results = await Promise.allSettled(
      digestIds.map((emailDigestId) =>
        addCardToDigest({ cardId, emailDigestId }).unwrap(),
      ),
    );

    let totalAdded = 0;
    let totalAlreadyAdded = 0;
    let anyFailed = false;
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        totalAdded += r.value.added ?? 0;
        totalAlreadyAdded += r.value.alreadyAdded ?? 0;
      } else {
        anyFailed = true;
      }
    });

    return { totalAdded, totalAlreadyAdded, anyFailed };
  };

  const handleSuccess = ({
    totalAdded,
    totalAlreadyAdded,
    anyFailed,
  }: DigestPickerSubmitResult): void => {
    if (anyFailed) {
      notify({
        type: NotifierType.ERROR,
        message: t('Card:actions.addToDigest.error'),
        icon: <X size={tokens.iconSize.small} />,
      });
      return;
    }
    if (totalAdded === 0 && totalAlreadyAdded > 0) {
      notify({
        type: NotifierType.INFO,
        message: t('Card:actions.addToDigest.successAlreadyAdded'),
        icon: <Warning size={tokens.iconSize.small} />,
      });
    } else if (totalAdded > 1) {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Card:actions.addToDigest.successSplit', {
          count: totalAdded,
        }),
        icon: <Check size={tokens.iconSize.small} />,
      });
    } else {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Card:actions.addToDigest.successSingle'),
        icon: <Check size={tokens.iconSize.small} />,
      });
    }
    onClose();
  };

  return (
    <DigestPickerModal
      title={t('Card:actions.addToDigest.title')}
      contentHtml={contentHtml}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onClose={onClose}
    />
  );
};

export default AddToDigestModal;
